import React, { useState, useEffect } from 'react';
import { Calendar, Clock, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';
import { format, addDays, startOfDay, isSameDay, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { motion, AnimatePresence } from 'framer-motion';
import { useAvailableSlots } from '../../hooks/useAvailableSlots';
import { HOURLY_TIME_SLOTS } from '../../constants/timeSlots';
import { API_BASE_URL } from '../../api';

interface TimeSlotSelectorProps {
  serviceId: number | null;
  selectedDateTime: Date | null;
  onDateTimeSelect: (dateTime: Date) => void;
  className?: string;
}

const TimeSlotSelector: React.FC<TimeSlotSelectorProps> = ({
  serviceId,
  selectedDateTime,
  onDateTimeSelect,
  className = ''
}) => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(startOfDay(new Date()));
  const [firstAvailableDate, setFirstAvailableDate] = useState<Date | null>(null);
  const [hasAvailability, setHasAvailability] = useState<boolean>(true);
  const [isLoadingFirstDate, setIsLoadingFirstDate] = useState<boolean>(false);

  // Récupérer la première date disponible quand le service change
  useEffect(() => {
    if (!serviceId) {
      setFirstAvailableDate(null);
      setHasAvailability(true);
      return;
    }

    const fetchFirstAvailableDate = async () => {
      setIsLoadingFirstDate(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/services/${serviceId}/first-available-date?max_days=30`
        );
        
        if (!response.ok) {
          throw new Error('Erreur lors de la récupération de la première date disponible');
        }
        
        const data = await response.json();
        
        if (data.has_availability && data.first_available_date) {
          const firstDate = new Date(data.first_available_date);
          setFirstAvailableDate(firstDate);
          setSelectedDate(firstDate);
          setCurrentWeekStart(startOfDay(firstDate));
          setHasAvailability(true);
        } else {
          setFirstAvailableDate(null);
          setHasAvailability(false);
          // Garder la date du jour si aucun créneau disponible
          setSelectedDate(new Date());
          setCurrentWeekStart(startOfDay(new Date()));
        }
      } catch (error) {
        console.error('Erreur lors de la récupération de la première date disponible:', error);
        // En cas d'erreur, garder le comportement par défaut
        setFirstAvailableDate(null);
        setHasAvailability(true);
      } finally {
        setIsLoadingFirstDate(false);
      }
    };

    fetchFirstAvailableDate();
  }, [serviceId]);

  // Récupérer les créneaux disponibles pour le service et la date sélectionnée
  const { slots, loading, error } = useAvailableSlots(
    serviceId || 0,
    format(selectedDate, 'yyyy-MM-dd')
  );

  // Générer les 7 prochains jours à partir de currentWeekStart
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));

  // Créneaux horaires fixes alignés avec le backend (BookingService.hourly_slots)
  // Pas de 13h (pause déjeuner), créneaux de 1h chacun
  const timeSlots = HOURLY_TIME_SLOTS;

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
  };

  const handleTimeSelect = (timeSlot: string) => {
    const [hours, minutes] = timeSlot.split(':').map(Number);
    
    // Créer la date en timezone locale sans conversion UTC
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const day = selectedDate.getDate();
    const dateTime = new Date(year, month, day, hours, minutes, 0, 0);
    onDateTimeSelect(dateTime);
  };

  const isSlotAvailable = (timeSlot: string): boolean => {
    if (!slots || slots.length === 0) return false;
    
    return slots.some(slot => {
      const slotStart = parseISO(slot.start_time);
      const slotHour = format(slotStart, 'HH:mm');
      return slotHour === timeSlot;
    });
  };

  const isSlotSelected = (timeSlot: string): boolean => {
    if (!selectedDateTime) return false;
    
    const selectedHour = format(selectedDateTime, 'HH:mm');
    return selectedHour === timeSlot && isSameDay(selectedDateTime, selectedDate);
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    const days = direction === 'next' ? 7 : -7;
    setCurrentWeekStart(addDays(currentWeekStart, days));
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Sélection de date */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-playfair font-bold text-lg text-neutral-ink flex items-center">
            <Calendar className="mr-2" size={20} />
            Choisissez une date
          </h3>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => navigateWeek('prev')}
              className="p-2 rounded-lg hover:bg-neutral-offwhite transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
            <span className="font-nunito text-sm text-neutral-body min-w-[120px] text-center">
              {format(currentWeekStart, 'MMM yyyy', { locale: fr })}
            </span>
            <button
              type="button"
              onClick={() => navigateWeek('next')}
              className="p-2 rounded-lg hover:bg-neutral-offwhite transition-colors"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-2">
          {weekDays.map((day, index) => {
            const isSelected = isSameDay(day, selectedDate);
            const isToday = isSameDay(day, new Date());
            const isPast = day < startOfDay(new Date());

            return (
              <motion.button
                key={index}
                type="button"
                onClick={() => !isPast && handleDateSelect(day)}
                disabled={isPast}
                className={`
                  p-3 rounded-xl text-center transition-all duration-200 font-nunito
                  ${isPast 
                    ? 'bg-neutral-offwhite text-neutral-body/50 cursor-not-allowed' 
                    : isSelected
                      ? 'bg-brand text-neutral-offwhite ring-2 ring-brand'
                      : 'bg-white border-2 border-neutral-line hover:border-brand hover:bg-brand/10'
                  }
                `}
                whileHover={!isPast ? { scale: 1.02 } : {}}
                whileTap={!isPast ? { scale: 0.98 } : {}}
              >
                <div className="text-xs uppercase tracking-wide mb-1">
                  {format(day, 'EEE', { locale: fr })}
                </div>
                <div className={`text-lg font-bold ${isToday && !isSelected ? 'text-brand' : ''}`}>
                  {format(day, 'd')}
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Message d'alerte si aucun créneau disponible */}
      {serviceId && !hasAvailability && !isLoadingFirstDate && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-lg"
        >
          <div className="flex items-start">
            <AlertCircle className="text-amber-500 mr-3 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <h4 className="font-nunito font-semibold text-amber-800 mb-1">
                Aucun créneau disponible
              </h4>
              <p className="font-nunito text-sm text-amber-700">
                Malheureusement, aucun créneau n'est disponible pour ce service dans les 30 prochains jours.
                Veuillez nous contacter directement pour planifier votre rendez-vous.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Sélection d'horaire */}
      <AnimatePresence>
        {serviceId && hasAvailability && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="space-y-4"
          >
            <h3 className="font-playfair font-bold text-lg text-neutral-ink flex items-center">
              <Clock className="mr-2" size={20} />
              Choisissez un horaire
              <span className="ml-2 text-sm font-normal text-neutral-body">
                ({format(selectedDate, 'dd MMMM yyyy', { locale: fr })})
              </span>
            </h3>

            {(loading || isLoadingFirstDate) ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
              </div>
            ) : error ? (
              <div className="text-center py-8 text-red-500">
                <p>Erreur lors du chargement des créneaux</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {timeSlots.map((timeSlot) => {
                  const isAvailable = isSlotAvailable(timeSlot);
                  const isSelected = isSlotSelected(timeSlot);

                  return (
                    <motion.button
                      key={timeSlot}
                      type="button"
                      onClick={() => isAvailable && handleTimeSelect(timeSlot)}
                      disabled={!isAvailable}
                      className={`
                        p-3 rounded-xl text-center transition-all duration-200 font-nunito font-medium
                        ${!isAvailable
                          ? 'bg-neutral-offwhite text-neutral-body/50 cursor-not-allowed opacity-50'
                          : isSelected
                            ? 'bg-brand text-neutral-offwhite ring-2 ring-brand shadow-lg'
                            : 'bg-white border-2 border-neutral-line hover:border-brand hover:bg-brand/10 hover:shadow-md'
                        }
                      `}
                      whileHover={isAvailable ? { scale: 1.05 } : {}}
                      whileTap={isAvailable ? { scale: 0.95 } : {}}
                    >
                      <div className="text-sm">
                        {timeSlot}
                      </div>
                      {!isAvailable && (
                        <div className="text-xs mt-1 opacity-75">
                          Indisponible
                        </div>
                      )}
                    </motion.button>
                  );
                })}
              </div>
            )}

            {/* Légende */}
            <div className="flex items-center justify-center space-x-6 text-sm text-neutral-body pt-4 border-t border-neutral-line">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-brand rounded"></div>
                <span>Sélectionné</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-white border-2 border-neutral-line rounded"></div>
                <span>Disponible</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-neutral-offwhite rounded opacity-50"></div>
                <span>Indisponible</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {!serviceId && (
        <div className="text-center py-8 text-neutral-body">
          <Clock className="mx-auto mb-2" size={32} />
          <p>Sélectionnez d'abord un service pour voir les créneaux disponibles</p>
        </div>
      )}
    </div>
  );
};

export default TimeSlotSelector;
